import { flattenText } from './helpers';
import { gettingStartedGuides } from './modules/gettingStarted';
import { dashboardGuides } from './modules/dashboard';
import { bookingGuides } from './modules/booking';
import { housekeepingGuides } from './modules/housekeeping';
import { posGuides } from './modules/pos';
import { stockGuides, roomInventoryGuides } from './modules/stockAndInventory';
import { financeGuides, reportsGuides } from './modules/financeAndReports';
import { customerGuides } from './modules/customer';
import { distributionGuides, pricingGuides } from './modules/distributionAndPricing';
import { configurationGuides } from './modules/configuration';
import { orgAndNexaStayGuides, referenceGuides } from './modules/orgAndReference';

/** Module navigation — only features that exist in RoomNexa today */
export const userGuideModules = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'Welcome, first login, and property go-live sequence.',
    accent: '#FF6B35',
    icon: 'PlayArrow',
    guideIds: gettingStartedGuides.map((g) => g.id),
  },
  {
    id: 'organisation',
    title: 'Organisation',
    description: 'Hotelier, org dashboard, settings, users & roles.',
    accent: '#4ce1b6',
    icon: 'Business',
    guideIds: ['org-hotelier', 'org-dashboard', 'org-settings', 'users-roles'],
  },
  {
    id: 'dashboard',
    title: 'Dashboard & Insights',
    description: 'Home calendar, quick book, tasks, and analytics dashboards.',
    accent: '#5B8DEF',
    icon: 'Dashboard',
    guideIds: dashboardGuides.map((g) => g.id),
  },
  {
    id: 'booking',
    title: 'Booking Management',
    description: 'Reservations, walk-ins, allocation, check-in/out, and booking ops.',
    accent: '#4A90E2',
    icon: 'CalendarToday',
    guideIds: bookingGuides.map((g) => g.id),
  },
  {
    id: 'housekeeping',
    title: 'Housekeeping',
    description: 'Room status, cleanings, staff, maintenance, lost & found.',
    accent: '#2BBBAD',
    icon: 'CleaningServices',
    guideIds: housekeepingGuides.map((g) => g.id),
  },
  {
    id: 'pos',
    title: 'Restaurant & POS',
    description: 'Floor ops, menu, orders, tables, QR ordering, payments.',
    accent: '#E67E22',
    icon: 'Restaurant',
    guideIds: posGuides.map((g) => g.id),
  },
  {
    id: 'stock',
    title: 'Stock',
    description: 'Consumables: items, purchases, movements, suppliers, reports.',
    accent: '#27AE60',
    icon: 'Inventory2',
    guideIds: stockGuides.map((g) => g.id),
  },
  {
    id: 'room-inventory',
    title: 'Room Inventory',
    description: 'Sellable room availability, lists, and daily inventory grid.',
    accent: '#F39C12',
    icon: 'Hotel',
    guideIds: roomInventoryGuides.map((g) => g.id),
  },
  {
    id: 'finance',
    title: 'Finance',
    description: 'Payments, cash counter, daily rates, draft invoices.',
    accent: '#8E44AD',
    icon: 'Payments',
    guideIds: financeGuides.map((g) => g.id),
  },
  {
    id: 'reports',
    title: 'Reports',
    description: 'Bookings, restaurant, customers, inventory, payments, GST, police log.',
    accent: '#C0392B',
    icon: 'BarChart',
    guideIds: reportsGuides.map((g) => g.id),
  },
  {
    id: 'customer',
    title: 'Customer Hub',
    description: 'Guest profiles, history, documents, and retention campaigns.',
    accent: '#9B59B6',
    icon: 'People',
    guideIds: customerGuides.map((g) => g.id),
  },
  {
    id: 'distribution',
    title: 'Distribution',
    description: 'Channels, mapping, allotments, blocks, NexaStay setup.',
    accent: '#16A085',
    icon: 'Hub',
    guideIds: distributionGuides.map((g) => g.id),
  },
  {
    id: 'dynamic-pricing',
    title: 'Dynamic Pricing',
    description: 'Rules, recommendations, competitors, simulate, forecast, history.',
    accent: '#D35400',
    icon: 'TrendingUp',
    guideIds: pricingGuides.map((g) => g.id),
  },
  {
    id: 'configuration',
    title: 'Configuration',
    description: 'Property masters: rooms, rates, taxes, media, policies, users.',
    accent: '#7F8C8D',
    icon: 'Settings',
    guideIds: configurationGuides.map((g) => g.id),
  },
  {
    id: 'nexastay',
    title: 'NexaStay',
    description: 'Guest-facing direct booking experience.',
    accent: '#1ABC9C',
    icon: 'Language',
    guideIds: ['nexastay-guest'],
  },
  {
    id: 'reference',
    title: 'Reference',
    description: 'Glossary, system requirements, troubleshooting, support, best practices.',
    accent: '#34495E',
    icon: 'MenuBook',
    guideIds: referenceGuides.map((g) => g.id),
  },
];

export const allGuides = [
  ...gettingStartedGuides,
  ...dashboardGuides,
  ...bookingGuides,
  ...housekeepingGuides,
  ...posGuides,
  ...stockGuides,
  ...roomInventoryGuides,
  ...financeGuides,
  ...reportsGuides,
  ...customerGuides,
  ...distributionGuides,
  ...pricingGuides,
  ...configurationGuides,
  ...orgAndNexaStayGuides,
  ...referenceGuides,
];

const guideById = Object.fromEntries(allGuides.map((g) => [g.id, g]));

export function getGuide(id) {
  return guideById[id] || null;
}

export function getModule(id) {
  return userGuideModules.find((m) => m.id === id) || null;
}

export function getModuleGuides(moduleId) {
  const mod = getModule(moduleId);
  if (!mod) return [];
  return mod.guideIds.map((id) => guideById[id]).filter(Boolean);
}

/** Flat ordered list for prev/next navigation */
export const guideOrder = userGuideModules.flatMap((m) => m.guideIds);

export function getAdjacentGuides(guideId) {
  const idx = guideOrder.indexOf(guideId);
  if (idx < 0) return { prev: null, next: null };
  return {
    prev: idx > 0 ? guideById[guideOrder[idx - 1]] : null,
    next: idx < guideOrder.length - 1 ? guideById[guideOrder[idx + 1]] : null,
  };
}

function guideSearchBlob(guide) {
  const sectionText = (guide.sections || [])
    .map((s) => `${s.title} ${flattenText(s.blocks)}`)
    .join(' ');
  return [
    guide.title,
    guide.summary,
    guide.pathHint,
    ...(guide.keywords || []),
    sectionText,
  ]
    .join(' ')
    .toLowerCase();
}

const searchIndex = allGuides.map((guide) => ({
  id: guide.id,
  moduleId: guide.moduleId,
  title: guide.title,
  summary: guide.summary,
  pathHint: guide.pathHint,
  keywords: guide.keywords || [],
  blob: guideSearchBlob(guide),
}));

/**
 * Ranked client-side search across titles, keywords, modules, and body text.
 */
export function searchUserGuide(query, limit = 40) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return [];
  const terms = q.split(/\s+/).filter(Boolean);

  const scored = searchIndex
    .map((item) => {
      let score = 0;
      const title = item.title.toLowerCase();
      const summary = (item.summary || '').toLowerCase();
      terms.forEach((term) => {
        if (title === term) score += 100;
        else if (title.startsWith(term)) score += 60;
        else if (title.includes(term)) score += 40;
        if (item.keywords.some((k) => k.toLowerCase().includes(term))) score += 35;
        if (summary.includes(term)) score += 15;
        if (item.blob.includes(term)) score += 5;
        if (item.moduleId.replace(/-/g, ' ').includes(term)) score += 10;
      });
      return { ...item, score };
    })
    .filter((i) => i.score > 0)
    .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title));

  return scored.slice(0, limit).map(({ blob, ...rest }) => rest);
}

export const userGuideMeta = {
  title: 'Roomnexa User Guide',
  subtitle: 'Enterprise documentation for every live Roomnexa PMS feature.',
  searchPlaceholder: 'Search features, actions, settings, errors…',
};
